import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { observer } from "mobx-react";
import {
  Box,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore, Refresh } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useStores } from "../../../../use-stores";
import {
  defaultOpsTatAppliedFilters,
  OPS_TAT_FILTER_TITLES,
  OpsTatAppliedFilters,
  OpsTatPath,
} from "../../../../../../models/Dashboard/OpsTat/OpsTatFilters";
import { FilterValue } from "../../../../../../models/Filters/FilterValue";
import { SortType } from "../../../../../../models/SortType";
import { NewOperationsDashboardStore } from "../../../../stores/dashboards/NewOperationsDashboardStore";
import { trackOpsTatAction } from "../../../../../../analytics/opsTatTracking";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import AppSearchDropdownMulti from "../../../../../../views/components/common/filter/AppSearchDropdownMulti";
import { DateRangePickerNew } from "../../../../../../views/components/common/textFields/DatePickerRangeNew";

const SEARCH_DEBOUNCE_MS = 400;
const ALLOWED_ORDER_TYPE_IDS = new Set(["1", "2", "3", "4", "5", "6"]);

const PROJECT_STATUS_OPTIONS: FilterValue[] = [
  { id: "both", value: "Both" },
  { id: "active", value: "Active" },
  { id: "archived", value: "Archived" },
];

const EXCLUDED_OPTIONS: FilterValue[] = [
  { id: "false", value: "No" },
  { id: "true", value: "Yes" },
];

const INCLUDE_RUNNING_OPTIONS: FilterValue[] = [
  { id: "false", value: "No" },
  { id: "true", value: "Yes" },
];

type DatePreset = "7d" | "30d" | "90d" | "6m" | "fy";

const DATE_PRESETS: Array<{ id: DatePreset; label: string }> = [
  { id: "7d", label: "7d" },
  { id: "30d", label: "30d" },
  { id: "90d", label: "90d" },
  { id: "6m", label: "6m" },
  { id: "fy", label: "FY" },
];

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const getIndianFyStart = (today: Dayjs): Dayjs => {
  const fyYear = today.month() >= 3 ? today.year() : today.year() - 1;
  return dayjs(`${fyYear}-04-01`).startOf("day");
};

const getPresetRange = (preset: DatePreset): { start: Dayjs; end: Dayjs } => {
  const end = dayjs().endOf("day");
  if (preset === "7d") {
    return { start: dayjs().subtract(6, "day").startOf("day"), end };
  }
  if (preset === "90d") {
    return { start: dayjs().subtract(89, "day").startOf("day"), end };
  }
  if (preset === "6m") {
    return { start: dayjs().subtract(6, "month").startOf("day"), end };
  }
  if (preset === "fy") {
    return { start: getIndianFyStart(dayjs()), end };
  }
  return { start: dayjs().subtract(29, "day").startOf("day"), end };
};

const matchDatePreset = (start: Dayjs, end: Dayjs): DatePreset | null => {
  const presets: DatePreset[] = ["7d", "30d", "90d", "6m", "fy"];
  for (const preset of presets) {
    const range = getPresetRange(preset);
    if (start.isSame(range.start, "day") && end.isSame(range.end, "day")) {
      return preset;
    }
  }
  return null;
};

const parseIdList = (raw: string | null): number[] => {
  if (!raw) {
    return [];
  }
  return raw
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);
};

const idsToFilterValues = (ids: number[]): FilterValue[] =>
  ids.map((id) => ({ id: String(id), value: String(id) }));

const filterValuesToIds = (items: FilterValue[]): number[] =>
  items
    .map((item) => Number(item.id))
    .filter((id) => Number.isInteger(id) && id > 0);

const parsePath = (raw: string | null): OpsTatPath => {
  if (raw === "custom" || raw === "stock" || raw === "all") {
    return raw;
  }
  return "all";
};

const parseProjectStatus = (raw: string | null): boolean | null => {
  if (raw === "true") {
    return true;
  }
  if (raw === "false") {
    return false;
  }
  return null;
};

const projectStatusToOption = (status: boolean | null): FilterValue[] => {
  if (status === true) {
    return PROJECT_STATUS_OPTIONS.filter((item) => item.id === "archived");
  }
  if (status === false) {
    return PROJECT_STATUS_OPTIONS.filter((item) => item.id === "active");
  }
  return PROJECT_STATUS_OPTIONS.filter((item) => item.id === "both");
};

const excludedToOption = (excluded: boolean): FilterValue[] =>
  EXCLUDED_OPTIONS.filter((item) => item.id === String(excluded));

const parseLocalDay = (raw: string | null, fallback: Dayjs): Dayjs => {
  if (!raw) {
    return fallback;
  }
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.startOf("day") : fallback;
};

const parseSearchParamsToApplied = (
  searchParams: URLSearchParams,
): OpsTatAppliedFilters => {
  const defaults = defaultOpsTatAppliedFilters();
  const orderType = parseIdList(searchParams.get("order_type")).filter(
    (id) => id >= 1 && id <= 6,
  );

  return {
    start_date: parseLocalDay(
      searchParams.get("start_date"),
      defaults.start_date,
    ),
    end_date: searchParams.get("end_date")
      ? parseLocalDay(searchParams.get("end_date"), defaults.end_date).endOf(
          "day",
        )
      : defaults.end_date,
    order_id: parseIdList(searchParams.get("order_id")),
    order_type: orderType,
    project_poc_id: parseIdList(searchParams.get("project_poc_id")),
    sourcing_poc_id: parseIdList(searchParams.get("sourcing_poc_id")),
    vendor_id: parseIdList(searchParams.get("vendor_id")),
    path: parsePath(searchParams.get("path")),
    project_status: parseProjectStatus(searchParams.get("project_status")),
    excluded_orders_from_sourcing:
      searchParams.get("excluded_orders_from_sourcing") === "true",
    include_running: searchParams.get("include_running") === "true",
  };
};

const appliedToSearchParams = (
  applied: OpsTatAppliedFilters,
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set("start_date", applied.start_date.format("YYYY-MM-DD"));
  params.set("end_date", applied.end_date.format("YYYY-MM-DD"));
  params.set("path", applied.path);

  if (applied.order_id.length > 0) {
    params.set("order_id", applied.order_id.join(","));
  }
  if (applied.order_type.length > 0) {
    params.set("order_type", applied.order_type.join(","));
  }
  if (applied.project_poc_id.length > 0) {
    params.set("project_poc_id", applied.project_poc_id.join(","));
  }
  if (applied.sourcing_poc_id.length > 0) {
    params.set("sourcing_poc_id", applied.sourcing_poc_id.join(","));
  }
  if (applied.vendor_id.length > 0) {
    params.set("vendor_id", applied.vendor_id.join(","));
  }
  if (applied.project_status !== null) {
    params.set("project_status", String(applied.project_status));
  }
  if (applied.excluded_orders_from_sourcing) {
    params.set("excluded_orders_from_sourcing", "true");
  }
  if (applied.include_running) {
    params.set("include_running", "true");
  }

  return params;
};

const mergeSelectedIntoList = (
  list: FilterValue[],
  selected: FilterValue[],
): FilterValue[] => {
  const ids = new Set(list.map((item) => String(item.id)));
  const extras = selected.filter((item) => !ids.has(String(item.id)));
  return extras.length > 0 ? [...extras, ...list] : list;
};

const pillButtonSx = {
  minHeight: 32,
  height: 32,
  minWidth: "auto",
  px: 1.5,
};

const FilterField = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => {
  const theme = useTheme();
  return (
    <Box sx={{ width: "100%" }}>
      <CustomTypography
        variant="caption"
        fontWeight={600}
        color={theme.palette.customColors.greyTextColor}
      >
        {label}
      </CustomTypography>
      <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
  );
};

export interface OpsTatFiltersProps {
  store: NewOperationsDashboardStore;
}

const OpsTatFilters: React.FC<OpsTatFiltersProps> = ({ store }) => {
  const theme = useTheme();
  const { projectStore, orderStore, TaskTrackerStore, vendorStore } =
    useStores();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialApplied = useMemo(
    () => parseSearchParamsToApplied(searchParams),
    // Hydrate once from the incoming URL so shared links are not stripped.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [isSearchExpanded, setIsSearchExpanded] = useState(true);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const [draftStart, setDraftStart] = useState<Dayjs>(
    initialApplied.start_date,
  );
  const [draftEnd, setDraftEnd] = useState<Dayjs>(initialApplied.end_date);
  const [selectedOrderIds, setSelectedOrderIds] = useState<FilterValue[]>(
    idsToFilterValues(initialApplied.order_id),
  );
  const [selectedOrderTypes, setSelectedOrderTypes] = useState<FilterValue[]>(
    idsToFilterValues(initialApplied.order_type),
  );
  const [selectedProjectPocs, setSelectedProjectPocs] = useState<FilterValue[]>(
    idsToFilterValues(initialApplied.project_poc_id),
  );
  const [selectedSourcingPocs, setSelectedSourcingPocs] = useState<
    FilterValue[]
  >(idsToFilterValues(initialApplied.sourcing_poc_id));
  const [selectedVendors, setSelectedVendors] = useState<FilterValue[]>(
    idsToFilterValues(initialApplied.vendor_id),
  );
  const [selectedProjectStatus, setSelectedProjectStatus] = useState<
    FilterValue[]
  >(projectStatusToOption(initialApplied.project_status));
  const [selectedExcluded, setSelectedExcluded] = useState<FilterValue[]>(
    excludedToOption(initialApplied.excluded_orders_from_sourcing),
  );
  const [selectedIncludeRunning, setSelectedIncludeRunning] = useState<
    FilterValue[]
  >(excludedToOption(initialApplied.include_running));
  const [orderSearchResults, setOrderSearchResults] = useState<FilterValue[]>(
    [],
  );
  const [vendorSearchResults, setVendorSearchResults] = useState<FilterValue[]>(
    [],
  );

  const orderSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const adminSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const vendorSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const orderTypeOptions = useMemo(
    () =>
      (projectStore.orderType ?? []).filter((item) =>
        ALLOWED_ORDER_TYPE_IDS.has(String(item.id)),
      ),
    [projectStore.orderType],
  );

  const orderOptions = useMemo(
    () => mergeSelectedIntoList(orderSearchResults, selectedOrderIds),
    [orderSearchResults, selectedOrderIds],
  );

  const adminOptions = useMemo(
    () =>
      (TaskTrackerStore.admins ?? []).map((admin) => ({
        id: String(admin.id),
        value: admin.name,
      })),
    [TaskTrackerStore.admins],
  );

  const projectPocOptions = useMemo(
    () => mergeSelectedIntoList(adminOptions, selectedProjectPocs),
    [adminOptions, selectedProjectPocs],
  );

  const sourcingPocOptions = useMemo(
    () => mergeSelectedIntoList(adminOptions, selectedSourcingPocs),
    [adminOptions, selectedSourcingPocs],
  );

  const vendorOptions = useMemo(
    () => mergeSelectedIntoList(vendorSearchResults, selectedVendors),
    [vendorSearchResults, selectedVendors],
  );

  const activePreset = matchDatePreset(draftStart, draftEnd);
  const appliedUrlKey = [
    store.appliedFilters.start_date.format("YYYY-MM-DD"),
    store.appliedFilters.end_date.format("YYYY-MM-DD"),
    store.appliedFilters.order_id.join(","),
    store.appliedFilters.order_type.join(","),
    store.appliedFilters.project_poc_id.join(","),
    store.appliedFilters.sourcing_poc_id.join(","),
    store.appliedFilters.vendor_id.join(","),
    store.appliedFilters.path,
    String(store.appliedFilters.project_status),
    String(store.appliedFilters.excluded_orders_from_sourcing),
    String(store.appliedFilters.include_running),
  ].join("|");

  useEffect(() => {
    return () => {
      if (orderSearchTimerRef.current) {
        clearTimeout(orderSearchTimerRef.current);
      }
      if (adminSearchTimerRef.current) {
        clearTimeout(adminSearchTimerRef.current);
      }
      if (vendorSearchTimerRef.current) {
        clearTimeout(vendorSearchTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        setInlineError(null);
        await projectStore.getTypeOfOrder();
        const labeledOrderTypes = (projectStore.orderType ?? [])
          .filter((item) => ALLOWED_ORDER_TYPE_IDS.has(String(item.id)))
          .filter((item) =>
            initialApplied.order_type.includes(Number(item.id)),
          );
        if (labeledOrderTypes.length > 0) {
          setSelectedOrderTypes(labeledOrderTypes);
        }
        store.setAppliedFilters(initialApplied);
        trackOpsTatAction("dashboard_view", initialApplied);
        await store.refreshDashboard(initialApplied);
      } catch (error) {
        setInlineError(
          store.summaryError ||
            getErrorMessage(error, "Failed to load ops TAT filters."),
        );
      }
    };

    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orderTypeOptions.length === 0) {
      return;
    }
    setSelectedOrderTypes((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const labeled = orderTypeOptions.filter((item) =>
        prev.some((selected) => String(selected.id) === String(item.id)),
      );
      if (labeled.length === 0) {
        return prev;
      }
      const unchanged =
        labeled.length === prev.length &&
        labeled.every(
          (item, index) =>
            item.id === prev[index].id && item.value === prev[index].value,
        );
      return unchanged ? prev : labeled;
    });
  }, [orderTypeOptions]);

  useEffect(() => {
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    try {
      setSearchParams(appliedToSearchParams(store.appliedFilters), {
        replace: true,
      });
    } catch (error) {
      setInlineError(
        getErrorMessage(error, "Failed to update the filter URL."),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedUrlKey, setSearchParams]);

  const buildAppliedFromDraft = (): OpsTatAppliedFilters => {
    const projectStatusId = selectedProjectStatus[0]?.id;
    const excludedId = selectedExcluded[0]?.id;
    const includeRunningId = selectedIncludeRunning[0]?.id;

    return {
      start_date: draftStart.startOf("day"),
      end_date: draftEnd.endOf("day"),
      order_id: filterValuesToIds(selectedOrderIds),
      order_type: filterValuesToIds(selectedOrderTypes).filter(
        (id) => id >= 1 && id <= 6,
      ),
      project_poc_id: filterValuesToIds(selectedProjectPocs),
      sourcing_poc_id: filterValuesToIds(selectedSourcingPocs),
      vendor_id: filterValuesToIds(selectedVendors),
      path: store.appliedFilters.path,
      project_status:
        projectStatusId === "archived"
          ? true
          : projectStatusId === "active"
            ? false
            : null,
      excluded_orders_from_sourcing: excludedId === "true",
      include_running: includeRunningId === "true",
    };
  };

  const validateApplied = (applied: OpsTatAppliedFilters): string | null => {
    if (applied.order_id.length > 0) {
      return null;
    }
    if (!applied.start_date || !applied.end_date) {
      return "Start date and end date are required.";
    }
    if (applied.start_date.isAfter(applied.end_date, "day")) {
      return "Start date must be on or before end date.";
    }
    return null;
  };

  const applyFilters = async (applied: OpsTatAppliedFilters) => {
    try {
      const validationError = validateApplied(applied);
      if (validationError) {
        setInlineError(validationError);
        return;
      }

      setInlineError(null);
      store.setAppliedFilters(applied);
      await store.refreshDashboard(applied);
    } catch (error) {
      setInlineError(
        store.summaryError ||
          getErrorMessage(error, "Failed to fetch ops TAT summary."),
      );
    }
  };

  const handleRefresh = async () => {
    try {
      setInlineError(null);
      await store.refreshData();
    } catch (error) {
      setInlineError(
        store.summaryError ||
          getErrorMessage(error, "Failed to refresh ops TAT summary."),
      );
    }
  };

  const handleApply = () => {
    try {
      const applied = buildAppliedFromDraft();
      const validationError = validateApplied(applied);
      if (validationError) {
        setInlineError(validationError);
        return;
      }
      trackOpsTatAction("filter_apply", applied);
      void applyFilters(applied);
    } catch (error) {
      setInlineError(
        getErrorMessage(error, "Failed to apply ops TAT filters."),
      );
    }
  };

  const handleClear = () => {
    try {
      const defaults = defaultOpsTatAppliedFilters();
      const applied: OpsTatAppliedFilters = {
        ...defaults,
        path: store.appliedFilters.path,
      };

      setDraftStart(applied.start_date);
      setDraftEnd(applied.end_date);
      setSelectedOrderIds([]);
      setSelectedOrderTypes([]);
      setSelectedProjectPocs([]);
      setSelectedSourcingPocs([]);
      setSelectedVendors([]);
      setSelectedProjectStatus(projectStatusToOption(applied.project_status));
      setSelectedExcluded(
        excludedToOption(applied.excluded_orders_from_sourcing),
      );
      setSelectedIncludeRunning(excludedToOption(applied.include_running));
      setInlineError(null);
      trackOpsTatAction("filter_clear", applied);
      void applyFilters(applied);
    } catch (error) {
      setInlineError(
        getErrorMessage(error, "Failed to reset ops TAT filters."),
      );
    }
  };

  const handlePresetClick = (preset: DatePreset) => {
    try {
      const range = getPresetRange(preset);
      setDraftStart(range.start);
      setDraftEnd(range.end);

      const appliedPreset = matchDatePreset(
        store.appliedFilters.start_date,
        store.appliedFilters.end_date,
      );
      if (appliedPreset === preset) {
        return;
      }

      const applied: OpsTatAppliedFilters = {
        ...store.appliedFilters,
        start_date: range.start.startOf("day"),
        end_date: range.end.endOf("day"),
      };
      trackOpsTatAction("date_preset_select", applied, {
        preset,
        context: { date_preset: preset },
      });
      void applyFilters(applied);
    } catch (error) {
      setInlineError(getErrorMessage(error, "Failed to set the date preset."));
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    try {
      if (range.startDate && range.endDate) {
        const start = range.startDate.startOf("day");
        const end = range.endDate.endOf("day");
        setDraftStart(start);
        setDraftEnd(end);
        trackOpsTatAction(
          "date_range_change",
          {
            ...store.appliedFilters,
            start_date: start,
            end_date: end,
          },
          {
            context: {
              start_date: start.format("YYYY-MM-DD"),
              end_date: end.format("YYYY-MM-DD"),
            },
          },
        );
      }
    } catch (error) {
      setInlineError(
        getErrorMessage(error, "Failed to set the custom date range."),
      );
    }
  };

  const fetchOrderIds = useCallback(
    async (searchText: string) => {
      try {
        const text = searchText.trim();
        if (!text) {
          setOrderSearchResults([]);
          return;
        }
        await orderStore.searchOrderIds(text);
        setOrderSearchResults(
          (orderStore.orderIds ?? []).map((id) => ({
            id: String(id),
            value: String(id),
          })),
        );
      } catch (error) {
        setOrderSearchResults([]);
        setInlineError(getErrorMessage(error, "Failed to search order IDs."));
      }
    },
    [orderStore],
  );

  const fetchAdmins = useCallback(
    async (searchText: string) => {
      try {
        await TaskTrackerStore.getAdminBySearch(searchText);
      } catch (error) {
        setInlineError(getErrorMessage(error, "Failed to search admins."));
      }
    },
    [TaskTrackerStore],
  );

  const fetchVendors = useCallback(
    async (searchText: string) => {
      try {
        const text = searchText.trim();
        if (!text) {
          setVendorSearchResults([]);
          return;
        }
        await vendorStore.getVendors({
          page_no: 1,
          limit: 30,
          search_text: text,
          sortBy: "created_date",
          sortType: SortType.DESC,
        });
        setVendorSearchResults(
          (vendorStore.vendors ?? []).map((vendor) => ({
            id: String(vendor.id),
            value: vendor.name || String(vendor.id),
          })),
        );
      } catch (error) {
        setVendorSearchResults([]);
        setInlineError(getErrorMessage(error, "Failed to search vendors."));
      }
    },
    [vendorStore],
  );

  const handleOrderSearch = (searchText?: string) => {
    if (orderSearchTimerRef.current) {
      clearTimeout(orderSearchTimerRef.current);
    }
    const text = searchText?.trim() ?? "";
    if (!text) {
      setOrderSearchResults([]);
      return;
    }
    orderSearchTimerRef.current = setTimeout(() => {
      void fetchOrderIds(text);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleAdminSearch = (searchText?: string) => {
    if (adminSearchTimerRef.current) {
      clearTimeout(adminSearchTimerRef.current);
    }
    const text = searchText?.trim() ?? "";
    if (text.length > 0 && text.length <= 2) {
      return;
    }
    adminSearchTimerRef.current = setTimeout(() => {
      void fetchAdmins(text);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleVendorSearch = (searchText?: string) => {
    if (vendorSearchTimerRef.current) {
      clearTimeout(vendorSearchTimerRef.current);
    }
    const text = searchText?.trim() ?? "";
    if (!text) {
      setVendorSearchResults([]);
      return;
    }
    vendorSearchTimerRef.current = setTimeout(() => {
      void fetchVendors(text);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleAdminOpen = useCallback(() => {
    void fetchAdmins("");
  }, [fetchAdmins]);

  const displayedError = inlineError || store.summaryError;

  return (
    <Paper
      sx={{
        mt: 3,
        p: { xs: 2, md: 3 },
        borderRadius: 2,
        boxShadow:
          "rgba(0, 0, 0, 0.15) 0px 4px 24px, rgba(0, 0, 0, 0.05) 0px 8px 32px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
          }}
        >
          <CustomTypography variant="heading3">Operations TAT</CustomTypography>
          <CustomTypography
            variant="body2"
            color={theme.palette.customColors?.greyTextColor}
          >
            Cycle time, stage turnaround, and dispatch against SLA
          </CustomTypography>
        </Box>
        <AppButton
          label={store.summaryLoading ? "Refreshing..." : "Refresh"}
          variant="secondary"
          startIcon={<Refresh />}
          onClick={() => void handleRefresh()}
          disabled={store.summaryLoading}
          customStyles={{ minHeight: 36, height: 36, px: 2 }}
        />
      </Box>

      <Paper
        variant="outlined"
        sx={{
          mt: 2,
          borderRadius: 2,
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => {
            const nextOpen = !isSearchExpanded;
            setIsSearchExpanded(nextOpen);
            trackOpsTatAction("search_panel_toggle", store.appliedFilters, {
              expanded: nextOpen,
              context: { filters_expanded: nextOpen },
            });
          }}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 0.5,
            cursor: "pointer",
            borderBottom: isSearchExpanded ? "1px solid" : "none",
            borderColor: "divider",
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <CustomTypography variant="heading6">Search</CustomTypography>
          <IconButton size="small">
            {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
          <Grid2 container spacing={2} columns={12} padding={2}>
            <Grid2 size={{ xs: 12 }}>
              <FilterField label="Date">
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {DATE_PRESETS.map((preset) => (
                    <AppButton
                      key={preset.id}
                      label={preset.label}
                      variant={
                        activePreset === preset.id
                          ? "primary"
                          : "transparentPrimary"
                      }
                      onClick={() => handlePresetClick(preset.id)}
                      disabled={store.summaryLoading}
                      customStyles={pillButtonSx}
                    />
                  ))}
                  <Box sx={{ width: { xs: "100%", sm: 260 }, flexShrink: 0 }}>
                    <DateRangePickerNew
                      startDate={draftStart}
                      endDate={draftEnd}
                      onRangeSelect={handleRangeSelect}
                    />
                  </Box>
                </Box>
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label={OPS_TAT_FILTER_TITLES.order_id}>
                <AppSearchDropdownMulti
                  title={OPS_TAT_FILTER_TITLES.order_id}
                  isMultiSelect={true}
                  local={false}
                  value={selectedOrderIds}
                  list={orderOptions}
                  handleSearch={handleOrderSearch}
                  handleSelect={(items: FilterValue[]) => {
                    setSelectedOrderIds(items);
                    trackOpsTatAction(
                      "filter_order_id_change",
                      store.appliedFilters,
                      {
                        context: { order_id_count: items.length },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label={OPS_TAT_FILTER_TITLES.order_type}>
                <AppSearchDropdownMulti
                  title={OPS_TAT_FILTER_TITLES.order_type}
                  isMultiSelect={true}
                  local={true}
                  value={selectedOrderTypes}
                  list={orderTypeOptions}
                  handleSelect={(items: FilterValue[]) => {
                    const next = items.filter((item) =>
                      ALLOWED_ORDER_TYPE_IDS.has(String(item.id)),
                    );
                    setSelectedOrderTypes(next);
                    trackOpsTatAction(
                      "filter_order_type_change",
                      store.appliedFilters,
                      {
                        context: { order_type_count: next.length },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label={OPS_TAT_FILTER_TITLES.project_poc_id}>
                <AppSearchDropdownMulti
                  title={OPS_TAT_FILTER_TITLES.project_poc_id}
                  isMultiSelect={true}
                  local={false}
                  value={selectedProjectPocs}
                  list={projectPocOptions}
                  handleSearch={handleAdminSearch}
                  onOpen={handleAdminOpen}
                  handleSelect={(items: FilterValue[]) => {
                    setSelectedProjectPocs(items);
                    trackOpsTatAction(
                      "filter_project_poc_change",
                      store.appliedFilters,
                      {
                        context: { project_poc_count: items.length },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label={OPS_TAT_FILTER_TITLES.sourcing_poc_id}>
                <AppSearchDropdownMulti
                  title={OPS_TAT_FILTER_TITLES.sourcing_poc_id}
                  isMultiSelect={true}
                  local={false}
                  value={selectedSourcingPocs}
                  list={sourcingPocOptions}
                  handleSearch={handleAdminSearch}
                  onOpen={handleAdminOpen}
                  handleSelect={(items: FilterValue[]) => {
                    setSelectedSourcingPocs(items);
                    trackOpsTatAction(
                      "filter_sourcing_poc_change",
                      store.appliedFilters,
                      {
                        context: { sourcing_poc_count: items.length },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label={OPS_TAT_FILTER_TITLES.vendor_id}>
                <AppSearchDropdownMulti
                  title={OPS_TAT_FILTER_TITLES.vendor_id}
                  isMultiSelect={true}
                  local={false}
                  value={selectedVendors}
                  list={vendorOptions}
                  handleSearch={handleVendorSearch}
                  handleSelect={(items: FilterValue[]) => {
                    setSelectedVendors(items);
                    trackOpsTatAction(
                      "filter_vendor_change",
                      store.appliedFilters,
                      {
                        context: { vendor_id_count: items.length },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label={OPS_TAT_FILTER_TITLES.project_status}>
                <AppSearchDropdownMulti
                  title={OPS_TAT_FILTER_TITLES.project_status}
                  isMultiSelect={false}
                  local={true}
                  value={selectedProjectStatus}
                  list={PROJECT_STATUS_OPTIONS}
                  handleSelect={(items: FilterValue[]) => {
                    const next =
                      items.length > 0
                        ? items
                        : PROJECT_STATUS_OPTIONS.filter(
                            (item) => item.id === "both",
                          );
                    setSelectedProjectStatus(next);
                    trackOpsTatAction(
                      "filter_project_status_change",
                      store.appliedFilters,
                      {
                        context: {
                          project_status: String(next[0]?.id ?? "both"),
                        },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField
                label={OPS_TAT_FILTER_TITLES.excluded_orders_from_sourcing}
              >
                <AppSearchDropdownMulti
                  title={OPS_TAT_FILTER_TITLES.excluded_orders_from_sourcing}
                  isMultiSelect={false}
                  local={true}
                  value={selectedExcluded}
                  list={EXCLUDED_OPTIONS}
                  handleSelect={(items: FilterValue[]) => {
                    const next =
                      items.length > 0
                        ? items
                        : EXCLUDED_OPTIONS.filter((item) => item.id === "false");
                    setSelectedExcluded(next);
                    trackOpsTatAction(
                      "filter_excluded_orders_change",
                      store.appliedFilters,
                      {
                        context: {
                          excluded_orders: next[0]?.id === "true",
                        },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label={OPS_TAT_FILTER_TITLES.include_running}>
                <AppSearchDropdownMulti
                  title={OPS_TAT_FILTER_TITLES.include_running}
                  isMultiSelect={false}
                  local={true}
                  value={selectedIncludeRunning}
                  list={INCLUDE_RUNNING_OPTIONS}
                  handleSelect={(items: FilterValue[]) => {
                    const next =
                      items.length > 0
                        ? items
                        : INCLUDE_RUNNING_OPTIONS.filter(
                            (item) => item.id === "false",
                          );
                    setSelectedIncludeRunning(next);
                    trackOpsTatAction(
                      "filter_include_running_change",
                      store.appliedFilters,
                      {
                        context: {
                          include_running: next[0]?.id === "true",
                        },
                      },
                    );
                  }}
                />
              </FilterField>
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <FilterField label={"\u00A0"}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear"
                    onClick={handleClear}
                    disabled={store.summaryLoading}
                    customStyles={{ width: "100%" }}
                  />
                  <AppButton
                    variant="primary"
                    label="Apply"
                    onClick={handleApply}
                    disabled={store.summaryLoading}
                    customStyles={{ width: "100%" }}
                  />
                </Box>
              </FilterField>
            </Grid2>
          </Grid2>
        </Collapse>
      </Paper>

      {displayedError && (
        <Box
          mt={2}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="4rem"
          textAlign="center"
          border="1px dashed"
          borderColor="divider"
          borderRadius={2}
          px={2}
          py={1.5}
        >
          <CustomTypography
            variant="heading6"
            fontWeight={500}
            color={theme.palette.customColors?.errorRed}
          >
            {displayedError}
          </CustomTypography>
        </Box>
      )}
    </Paper>
  );
};

export default observer(OpsTatFilters);
